/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Testing Plans Operations
 * 
 * Handles testing plan generation and release listing.
 */

import type { Issue } from '@shared/types/issues';
import { getProductServerBaseUrl } from './base';

export type TestingPlanScope = 'product' | 'release';

export interface TestingPlanResult {
  success: boolean;
  filePath?: string;
  fileName?: string;
  storyCount?: number;
  error?: string;
}

export interface ReleasesResult {
  success: boolean;
  releases: Issue[];
  error?: string;
}

export interface GeneratedFeatureArea {
  id: string;
  name: string;
  specFile: string;
  testCount: number;
}

export interface GenerateTestsResult {
  success: boolean;
  featureAreas?: GeneratedFeatureArea[];
  manifestPath?: string;
  error?: string;
}

export interface TestRunSummary {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
}

export interface TestFailure {
  testName: string;
  featureArea: string;
  error: string;
  userStoryId?: string;
}

export interface RunTestsResult {
  success: boolean;
  exitCode?: number | null;
  output?: string;
  errors?: string;
  testResults?: any;
  reportPath?: string;
  error?: string;
}

export interface TestResultsResponse {
  success: boolean;
  summary?: TestRunSummary;
  failures?: TestFailure[];
  reportPath?: string;
  error?: string;
}

export interface FeatureAreasResponse {
  success: boolean;
  featureAreas: GeneratedFeatureArea[];
  generatedAt?: string;
  error?: string;
}

export interface CreatedBug {
  id: string;
  title: string;
}

export interface CreateBugsResult {
  success: boolean;
  createdIssues?: CreatedBug[];
  error?: string;
}

/**
 * Generate a testing plan for a product or release
 */
export async function generateTestingPlan(
  scope: TestingPlanScope,
  productInfo: {
    id: string;
    title: string;
    description?: string;
    repositoryUrl?: string;
    repoPath?: string | null;
  },
  releaseId?: string
): Promise<TestingPlanResult> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/testing-plans/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repoUrl: productInfo.repositoryUrl,
        repoPath: productInfo.repoPath,
        scope,
        releaseId,
        productInfo: {
          id: productInfo.id,
          title: productInfo.title,
          description: productInfo.description,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to generate testing plan (${response.status})`,
      };
    }

    const result = await response.json();
    return {
      success: result.success,
      filePath: result.filePath,
      fileName: result.fileName,
      storyCount: result.storyCount,
      error: result.error,
    };
  } catch (error: any) {
    console.error('[Testing Plans] Error generating testing plan:', error);
    return {
      success: false,
      error: error.message || 'Failed to generate testing plan',
    };
  }
}

/**
 * Get releases for a product
 */
export async function getReleases(
  repoUrl?: string,
  repoPath?: string | null
): Promise<ReleasesResult> {
  try {
    const params = new URLSearchParams();
    if (repoUrl) params.append('repoUrl', repoUrl);
    if (repoPath) params.append('repoPath', repoPath);

    const url = `${getProductServerBaseUrl()}/api/testing-plans/releases?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        releases: [],
        error: errorData.error || `Failed to fetch releases (${response.status})`,
      };
    }

    const result = await response.json();
    return {
      success: result.success,
      releases: result.releases || [],
      error: result.error,
    };
  } catch (error: any) {
    console.error('[Testing Plans] Error fetching releases:', error);
    return {
      success: false,
      releases: [],
      error: error.message || 'Failed to fetch releases',
    };
  }
}

/**
 * Generate Playwright test files from a testing plan
 */
export async function generatePlaywrightTests(
  repoUrl?: string,
  repoPath?: string | null,
  baseUrl?: string
): Promise<GenerateTestsResult> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/testing-plans/generate-tests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repoUrl,
        repoPath,
        baseUrl,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to generate tests (${response.status})`,
      };
    }

    const result = await response.json();
    return {
      success: result.success,
      featureAreas: result.featureAreas,
      manifestPath: result.manifestPath,
      error: result.error,
    };
  } catch (error: any) {
    console.error('[Testing Plans] Error generating tests:', error);
    return {
      success: false,
      error: error.message || 'Failed to generate tests',
    };
  }
}

/**
 * Run Playwright tests
 */
export async function runPlaywrightTests(
  featureArea?: string
): Promise<RunTestsResult> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/testing-plans/run-tests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        featureArea,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to run tests (${response.status})`,
      };
    }

    const result = await response.json();
    return {
      success: result.success,
      exitCode: result.exitCode,
      output: result.output,
      errors: result.errors,
      testResults: result.testResults,
      reportPath: result.reportPath,
      error: result.error,
    };
  } catch (error: any) {
    console.error('[Testing Plans] Error running tests:', error);
    return {
      success: false,
      error: error.message || 'Failed to run tests',
    };
  }
}

/**
 * Get test results from the last run
 */
export async function getTestResults(): Promise<TestResultsResponse> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/testing-plans/test-results`);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get test results (${response.status})`,
      };
    }

    const result = await response.json();
    return {
      success: result.success,
      summary: result.summary,
      failures: result.failures,
      reportPath: result.reportPath,
      error: result.error,
    };
  } catch (error: any) {
    console.error('[Testing Plans] Error getting test results:', error);
    return {
      success: false,
      error: error.message || 'Failed to get test results',
    };
  }
}

/**
 * Get list of generated feature areas
 */
export async function getGeneratedFeatureAreas(): Promise<FeatureAreasResponse> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/testing-plans/feature-areas`);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        featureAreas: [],
        error: errorData.error || `Failed to get feature areas (${response.status})`,
      };
    }

    const result = await response.json();
    return {
      success: result.success,
      featureAreas: result.featureAreas || [],
      generatedAt: result.generatedAt,
      error: result.error,
    };
  } catch (error: any) {
    console.error('[Testing Plans] Error getting feature areas:', error);
    return {
      success: false,
      featureAreas: [],
      error: error.message || 'Failed to get feature areas',
    };
  }
}

/**
 * Create bug issues from test failures
 */
export async function createBugIssues(
  failures: TestFailure[],
  repoUrl?: string,
  repoPath?: string | null
): Promise<CreateBugsResult> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/testing-plans/create-bugs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repoUrl,
        repoPath,
        failures,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to create bugs (${response.status})`,
      };
    }

    const result = await response.json();
    return {
      success: result.success,
      createdIssues: result.createdIssues,
      error: result.error,
    };
  } catch (error: any) {
    console.error('[Testing Plans] Error creating bugs:', error);
    return {
      success: false,
      error: error.message || 'Failed to create bugs',
    };
  }
}
